import { Button, Drawer, FormField, Input, Switch } from '@ntconsult/aluma-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PercentageCheckFilterState } from '../hooks/usePercentageCheckFilters';
import type { PercentageCheckFilterOptions } from '../types/PercentageCheck';
import { MultiSelectFilter } from './MultiSelectFilter';

const TITLE_ID = 'percentage-check-filters-title';
const FORM_ID = 'percentage-check-filters-form';

type FiltersDrawerProps = {
  open: boolean;
  onClose: () => void;
  filterState: PercentageCheckFilterState;
  filterOptions: PercentageCheckFilterOptions | undefined;
};

type Draft = {
  professionalSearch: string;
  unitIds: number[];
  contractTypeIds: number[];
  contractStatusIds: number[];
  includeZero: boolean;
};

function draftFrom({ filters, state }: PercentageCheckFilterState): Draft {
  return {
    professionalSearch: filters.professionalSearch ?? '',
    unitIds: filters.unitIds ?? [],
    contractTypeIds: filters.contractTypeIds ?? [],
    contractStatusIds: filters.contractStatusIds ?? [],
    includeZero: state.includeZero,
  };
}

export function PercentageCheckFiltersDrawer({
  open,
  onClose,
  filterState,
  filterOptions,
}: FiltersDrawerProps) {
  const { t } = useTranslation(['alocacao', 'common']);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(filterState));
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(draftFrom(filterState));
  }

  const apply = () => {
    filterState.applyAdvanced(draft);
    onClose();
  };

  return (
    <Drawer open={open} onClose={onClose} size="md" ariaLabelledBy={TITLE_ID}>
      <Drawer.Header
        titleId={TITLE_ID}
        dismissLabel={t('common:close')}
        title={t('alocacao:consultas.conferenciaPercentual.filtros.titulo')}
        subtitle={t('alocacao:consultas.conferenciaPercentual.filtros.descricao')}
        onClose={onClose}
      />
      <Drawer.Body>
        <form
          id={FORM_ID}
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            apply();
          }}
        >
          <FormField label={t('alocacao:consultas.conferenciaPercentual.filtros.profissional')}>
            <Input
              value={draft.professionalSearch}
              placeholder={t('alocacao:consultas.filtros.nomeOuMatriculaPlaceholder')}
              onChange={(e) => setDraft((d) => ({ ...d, professionalSearch: e.target.value }))}
            />
          </FormField>
          <FormField label={t('alocacao:consultas.conferenciaPercentual.filtros.unidade')}>
            <MultiSelectFilter
              options={(filterOptions?.units ?? []).map((unit) => ({
                id: unit.id,
                label: unit.code,
              }))}
              value={draft.unitIds}
              onChange={(unitIds) => setDraft((d) => ({ ...d, unitIds }))}
            />
          </FormField>
          <FormField label={t('alocacao:consultas.conferenciaPercentual.filtros.tipoContrato')}>
            <MultiSelectFilter
              options={(filterOptions?.contractTypes ?? []).map((type) => ({
                id: type.id,
                label: type.name,
              }))}
              value={draft.contractTypeIds}
              onChange={(contractTypeIds) => setDraft((d) => ({ ...d, contractTypeIds }))}
            />
          </FormField>
          <FormField label={t('alocacao:consultas.conferenciaPercentual.filtros.situacaoContrato')}>
            <MultiSelectFilter
              options={(filterOptions?.contractStatuses ?? []).map((status) => ({
                id: status.id,
                label: status.name,
              }))}
              value={draft.contractStatusIds}
              onChange={(contractStatusIds) => setDraft((d) => ({ ...d, contractStatusIds }))}
            />
          </FormField>
          <Switch
            checked={draft.includeZero}
            onCheckedChange={(includeZero) => setDraft((d) => ({ ...d, includeZero }))}
            label={t('alocacao:consultas.conferenciaPercentual.incluirZero')}
          />
        </form>
      </Drawer.Body>
      <Drawer.Footer className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          {t('alocacao:filtros.cancelar')}
        </Button>
        <Button variant="primary" type="submit" form={FORM_ID}>
          {t('alocacao:filtros.aplicar')}
        </Button>
      </Drawer.Footer>
    </Drawer>
  );
}
